/**
 * Party — wallet, budget pools, loot.
 *
 * Read path: fetches the party snapshot + loot list + audit log via the
 * SessionRead HTTP lambda. Live updates: opens a CampaignSocket and listens
 * for party/patch broadcasts; wallet edits ship a party/update mutation
 * carrying the FULL new doc under a version check (see partyProtocol.ts).
 *
 * The page is intentionally dumb about shape: it defensive-renders whatever
 * the API returns, so a partially-seeded party or a pool with missing
 * optional fields won't crash the view.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AuditEntry,
  DEFAULT_SESSION_ID,
  getAuditLog,
  getPartySnapshot,
  PartySnapshot,
} from '../lib/sessionApi';
import {
  BudgetPool,
  LootItem,
  Party as PartyDoc,
  Wallet,
  unallocatedWallet,
} from '../lib/party';
import { CampaignSocket } from '../lib/campaignSocket';
import {
  PartyActions,
  PartyPatchPayload,
  PartyRejectedPayload,
  PartyServerTypes,
  PartyUpdateRequest,
} from '../lib/partyProtocol';

// Match Sheet.tsx — both pages subscribe to the same single-campaign map so
// they end up on the same WebSocket fan-out group.
const DEFAULT_MAP_ID = 'local-dev';

/**
 * Render a wallet as the four coin columns, muted when zero. Uses the
 * JetBrains Mono stack via the .num class from the design system.
 */
function WalletLine({ wallet }: { wallet: Wallet | undefined }) {
  const w = wallet ?? { cp: 0, sp: 0, gp: 0, pp: 0 };
  const coins: Array<[keyof Wallet, string]> = [
    ['pp', 'pp'],
    ['gp', 'gp'],
    ['sp', 'sp'],
    ['cp', 'cp'],
  ];
  return (
    <span className="num" style={{ display: 'inline-flex', gap: '0.75rem' }}>
      {coins.map(([k, label]) => (
        <span
          key={k}
          style={{
            color: w[k] > 0 ? 'var(--fg)' : 'var(--fg-muted)',
          }}
        >
          {w[k]} <span style={{ color: 'var(--fg-muted)' }}>{label}</span>
        </span>
      ))}
    </span>
  );
}

function PoolRow({ pool }: { pool: BudgetPool }) {
  const scope =
    pool.ownerScope === 'party'
      ? 'Party'
      : `Character · ${pool.ownerScope.character}`;
  return (
    <div
      style={{
        padding: '0.75rem 1rem',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        marginBottom: '0.5rem',
        opacity: pool.archived ? 0.5 : 1,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong>{pool.name}</strong>
        <span style={{ fontSize: '0.85rem', color: 'var(--fg-muted)' }}>{scope}</span>
      </div>
      {pool.description && (
        <p style={{ margin: '0.25rem 0', color: 'var(--fg-muted)', fontSize: '0.9rem' }}>
          {pool.description}
        </p>
      )}
      <div style={{ marginTop: '0.25rem' }}>
        <WalletLine wallet={pool.currentAmount} />
        {pool.targetAmount && (
          <span style={{ marginLeft: '0.75rem', color: 'var(--fg-muted)' }}>
            / <WalletLine wallet={pool.targetAmount} />
          </span>
        )}
      </div>
    </div>
  );
}

function LootRow({ item }: { item: LootItem }) {
  const display = item.identified ? item.name : `Unknown ${(item.tags?.[0]) ?? 'item'}`;
  return (
    <tr>
      <td>{display}</td>
      <td className="num" style={{ textAlign: 'right' }}>{item.quantity}</td>
      <td style={{ color: 'var(--fg-muted)' }}>{item.source ?? ''}</td>
      <td style={{ color: 'var(--fg-muted)' }}>{item.tags?.join(', ') ?? ''}</td>
    </tr>
  );
}

/**
 * Inline wallet deposit/withdraw form. Lets the user type a coin amount per
 * column and apply it as a signed delta against the current wallet — negative
 * values withdraw, positive values deposit. We submit the FULL new wallet
 * because that's the on-wire shape (party/update is a full-replace), but the
 * UI is delta-shaped because that's how players think about it ("got 50gp
 * from the chest", "spent 3sp at the inn").
 */
function WalletEditor({
  wallet,
  onSubmit,
}: {
  wallet: Wallet;
  onSubmit: (next: Wallet, reason?: string) => void;
}) {
  const [delta, setDelta] = useState<Wallet>({ cp: 0, sp: 0, gp: 0, pp: 0 });
  const [reason, setReason] = useState('');

  const apply = () => {
    const next: Wallet = {
      cp: wallet.cp + delta.cp,
      sp: wallet.sp + delta.sp,
      gp: wallet.gp + delta.gp,
      pp: wallet.pp + delta.pp,
    };
    if (next.cp < 0 || next.sp < 0 || next.gp < 0 || next.pp < 0) {
      // Local guard so we don't even ship a request the server would reject.
      return;
    }
    onSubmit(next, reason.trim() || undefined);
    setDelta({ cp: 0, sp: 0, gp: 0, pp: 0 });
    setReason('');
  };

  const allZero = delta.cp === 0 && delta.sp === 0 && delta.gp === 0 && delta.pp === 0;
  const coins: Array<keyof Wallet> = ['pp', 'gp', 'sp', 'cp'];

  return (
    <div
      style={{
        marginTop: '0.5rem',
        padding: '0.75rem',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        alignItems: 'flex-end',
      }}
    >
      {coins.map((c) => (
        <label key={c} style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
          <span style={{ color: 'var(--fg-muted)' }}>{c}</span>
          <input
            type="number"
            value={delta[c]}
            onChange={(e) =>
              setDelta((prev) => ({ ...prev, [c]: Number(e.target.value) || 0 }))
            }
            className="num"
            style={{ width: '5rem' }}
          />
        </label>
      ))}
      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem', flex: 1, minWidth: '12rem' }}>
        <span style={{ color: 'var(--fg-muted)' }}>reason (optional)</span>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. chest in goblin cave"
        />
      </label>
      <button onClick={apply} disabled={allZero}>
        Apply
      </button>
    </div>
  );
}

export default function Party() {
  const [snapshot, setSnapshot] = useState<PartySnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [auditCursor, setAuditCursor] = useState<string | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);

  const socketRef = useRef<CampaignSocket | null>(null);
  // Tracks the version we expect the next server patch to carry. Optimistic
  // sends bump this; rejections snap it back to whatever the server says.
  const pendingVersionRef = useRef<number>(0);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const data = await getPartySnapshot(DEFAULT_SESSION_ID);
      setSnapshot(data);
      pendingVersionRef.current = data.party.version;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load party');
    } finally {
      setLoading(false);
    }
  }, []);

  // Audit log loads independently of the party snapshot — it's a separate
  // partition read and a slow audit endpoint shouldn't block the party view
  // from rendering.
  const loadInitialAudit = useCallback(async () => {
    setAuditError(null);
    setAuditLoading(true);
    try {
      const page = await getAuditLog(DEFAULT_SESSION_ID, { limit: 50 });
      setAudit(page.entries);
      setAuditCursor(page.nextBefore);
    } catch (err) {
      setAuditError(err instanceof Error ? err.message : 'Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const loadOlderAudit = useCallback(async () => {
    if (!auditCursor) return;
    setAuditLoading(true);
    try {
      const page = await getAuditLog(DEFAULT_SESSION_ID, { limit: 50, before: auditCursor });
      setAudit((prev) => [...prev, ...page.entries]);
      setAuditCursor(page.nextBefore);
    } catch (err) {
      setAuditError(err instanceof Error ? err.message : 'Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  }, [auditCursor]);

  useEffect(() => {
    refresh();
    loadInitialAudit();
  }, [refresh, loadInitialAudit]);

  // Open the live socket once on mount. The party page subscribes via the
  // same scene/subscribe path as Sheet so the connection row gets its
  // SessionId bound — that's what makes the BySession GSI fan-out reach us.
  useEffect(() => {
    let cancelled = false;
    let cs: CampaignSocket | null = null;

    (async () => {
      try {
        cs = await CampaignSocket.connect({
          sessionId: DEFAULT_SESSION_ID,
          mapId: DEFAULT_MAP_ID,
        });
        if (cancelled) {
          cs.close();
          return;
        }
        socketRef.current = cs;

        cs.on<PartyPatchPayload>(PartyServerTypes.PartyPatch, (payload) => {
          setSnapshot((prev) => {
            if (!prev) return prev;
            // Out-of-order echo guard. Shouldn't happen on a single
            // connection but keeps us safe under reconnects.
            if (payload.version <= prev.party.version) return prev;
            pendingVersionRef.current = payload.version;
            return { ...prev, party: payload.party };
          });
          setConflict(null);
          // Refresh the audit log so the new entry shows up. Cheap — small
          // page, single round trip — and avoids us reproducing the audit
          // composer client-side.
          loadInitialAudit();
        });

        cs.on<PartyRejectedPayload>(PartyServerTypes.PartyRejected, (payload) => {
          if (payload.party) {
            setSnapshot((prev) =>
              prev ? { ...prev, party: payload.party as PartyDoc } : prev,
            );
            pendingVersionRef.current = payload.version;
          }
          setConflict(`Rejected: ${payload.reason}. Reconciled to v${payload.version}.`);
        });
      } catch (err) {
        if (!cancelled) {
          // Live updates are nice-to-have on the party page; the HTTP read
          // path already populated the view. Surface the failure quietly.
          setConflict(
            err instanceof Error
              ? `Live updates offline: ${err.message}`
              : 'Live updates offline',
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      cs?.close();
      socketRef.current = null;
    };
  }, [loadInitialAudit]);

  // Wallet/pool edits ship the FULL new party doc under a version check.
  // The client computes the new doc locally (apply the intended delta on top
  // of the current snapshot), the server overwrites under a conditional
  // PutItem, and the echoed party/patch confirms — same shape as Sheet's
  // slot updates. See PartyUpdateRequest for why we don't ship per-field
  // deltas.
  const sendPartyUpdate = useCallback(
    (next: { wallet: Wallet; pools: BudgetPool[] }, reason?: string) => {
      const cs = socketRef.current;
      const cur = snapshot;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: PartyUpdateRequest = {
        action: PartyActions.Update,
        expectedVersion: expected,
        wallet: next.wallet,
        pools: next.pools,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      // Optimistic local apply.
      setSnapshot((prev) =>
        prev
          ? {
              ...prev,
              party: {
                ...prev.party,
                version: expected + 1,
                wallet: next.wallet,
                pools: next.pools,
              },
            }
          : prev,
      );
    },
    [snapshot],
  );

  if (loading) {
    return <p>Loading party…</p>;
  }
  if (error) {
    return <p style={{ color: 'var(--danger, #e06c75)' }}>Error: {error}</p>;
  }
  if (!snapshot) {
    return <p>No party data.</p>;
  }

  const { party, loot } = snapshot;
  const unallocated = unallocatedWallet(party);
  const activePools = party.pools.filter((p) => !p.archived);
  const archivedPools = party.pools.filter((p) => p.archived);

  return (
    <div style={{ maxWidth: '900px' }}>
      <h1>Party</h1>

      <section style={{ marginBottom: '2rem' }}>
        <h2>Wallet</h2>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ color: 'var(--fg-muted)', marginRight: '0.5rem' }}>Total:</span>
          <WalletLine wallet={party.wallet} />
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <span style={{ color: 'var(--fg-muted)', marginRight: '0.5rem' }}>Unallocated:</span>
          <WalletLine wallet={unallocated} />
        </div>
        {conflict && (
          <p style={{ color: 'var(--danger, #e06c75)', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
            {conflict}
          </p>
        )}
        <WalletEditor
          wallet={party.wallet}
          onSubmit={(nextWallet, reason) =>
            sendPartyUpdate({ wallet: nextWallet, pools: party.pools }, reason)
          }
        />
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2>Budget pools</h2>
        {activePools.length === 0 && (
          <p style={{ color: 'var(--fg-muted)' }}>No active pools.</p>
        )}
        {activePools.map((p) => (
          <PoolRow key={p.id} pool={p} />
        ))}
        {archivedPools.length > 0 && (
          <details style={{ marginTop: '1rem' }}>
            <summary style={{ cursor: 'pointer', color: 'var(--fg-muted)' }}>
              Archived ({archivedPools.length})
            </summary>
            <div style={{ marginTop: '0.5rem' }}>
              {archivedPools.map((p) => (
                <PoolRow key={p.id} pool={p} />
              ))}
            </div>
          </details>
        )}
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2>Activity</h2>
        {auditError && (
          <p style={{ color: 'var(--danger, #e06c75)' }}>Audit error: {auditError}</p>
        )}
        {audit.length === 0 && !auditLoading && !auditError && (
          <p style={{ color: 'var(--fg-muted)' }}>No activity yet.</p>
        )}
        {audit.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {audit.map((e) => (
              <li
                key={e.id}
                style={{
                  padding: '0.5rem 0',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'baseline',
                }}
              >
                <span className="num" style={{ color: 'var(--fg-muted)', fontSize: '0.85rem', minWidth: '11rem' }}>
                  {new Date(e.ts).toLocaleString()}
                </span>
                <span style={{ color: 'var(--fg-muted)', minWidth: '7rem' }}>
                  {e.actorUsername}
                </span>
                <span style={{ flex: 1 }}>
                  {e.summary}
                  {e.reason && (
                    <span style={{ color: 'var(--fg-muted)', marginLeft: '0.5rem' }}>
                      ({e.reason})
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        {auditCursor && (
          <button
            onClick={loadOlderAudit}
            disabled={auditLoading}
            style={{ marginTop: '0.75rem' }}
          >
            {auditLoading ? 'Loading…' : 'Load older'}
          </button>
        )}
      </section>

      <section>
        <h2>Loot ({loot.length})</h2>
        {loot.length === 0 ? (
          <p style={{ color: 'var(--fg-muted)' }}>No loot yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th>Item</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th>Source</th>
                <th>Tags</th>
              </tr>
            </thead>
            <tbody>
              {loot.map((item) => (
                <LootRow key={item.id} item={item} />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
